import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Check, Lightbulb, RotateCw, Sparkles, Star } from 'lucide-react';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import {
  CARD_RARITIES,
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  CARD_TYPE_LABELS,
  type CardRarity,
} from '@jeraisonne/db/cards';
import { Button, cn } from '@jeraisonne/ui';
import { isAdmin, useAuth } from '@/lib/auth';
import { useCreateCard, useGenerateCard, useGenerateCardInfo } from '@/lib/cards/api';
import {
  CARD_IDEA_COUNTS,
  IDEA_JOB_LABELS,
  cardInputFromIdea,
  ideaSourceText,
  useCardIdeas,
  type CardIdea,
  type IdeaJobStep,
} from '@/lib/cards/ideas';
import { EMPTY_CARD_INPUT } from '@/features/cards/card-info-form';

interface IdeaJob {
  step: IdeaJobStep;
  cardId?: string;
  error?: string;
}

const subjectLabel = (value: string) => SUBJECTS.find((s) => s.value === value)?.label ?? value;

/**
 * Route admin `/tutor/cards/ideas` — l'IA propose des idées de SKILLCARDS pour
 * un niveau, une rareté et (facultatif) un thème / une matière ; l'admin coche
 * les meilleures et l'app crée chaque carte : « Générer la carte » (toutes les
 * informations), carte en brouillon, puis rédaction du recto. Une carte dont
 * le recto échoue existe quand même (bouton « Générer » sur sa fiche).
 */
export function TutorCardIdeasPage() {
  const { user, profile } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const ideasQuery = useCardIdeas();
  const generateInfo = useGenerateCardInfo();
  const createCard = useCreateCard();
  const generateCard = useGenerateCard();

  const [level, setLevel] = useState('3e');
  const [rarity, setRarity] = useState<CardRarity>('normale');
  const [theme, setTheme] = useState('');
  const [subject, setSubject] = useState('');
  const [count, setCount] = useState<number>(8);
  const [ideas, setIdeas] = useState<CardIdea[]>([]);
  // Contexte des idées affichées (le formulaire peut changer ensuite).
  const [ideasCtx, setIdeasCtx] = useState<{ level: string; rarity: CardRarity; theme?: string }>();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [jobs, setJobs] = useState<Record<number, IdeaJob>>({});
  const [running, setRunning] = useState(false);

  if (!admin) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 sm:py-8">
        <BackLink />
        <p className="text-ink-600 text-sm">Réservé aux administrateurs.</p>
      </div>
    );
  }

  async function propose() {
    setSelected(new Set());
    setJobs({});
    const ctx = { level, rarity, theme: theme.trim() || undefined };
    try {
      const list = await ideasQuery.mutateAsync({ ...ctx, subject, count });
      setIdeas(list);
      setIdeasCtx(ctx);
    } catch {
      /* message affiché via ideasQuery.error */
    }
  }

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  async function generateSelected() {
    if (!user || !ideasCtx) return;
    setRunning(true);
    const order = [...selected].filter((i) => jobs[i]?.step !== 'done').sort((a, b) => a - b);
    setJobs((prev) => {
      const next = { ...prev };
      for (const i of order) next[i] = { step: 'waiting' };
      return next;
    });
    const set = (i: number, job: IdeaJob) => setJobs((prev) => ({ ...prev, [i]: job }));
    // Une carte après l'autre : budget IA maîtrisé, progression lisible.
    for (const i of order) {
      const idea = ideas[i];
      if (!idea) continue;
      let cardId: string | undefined;
      try {
        set(i, { step: 'info' });
        const info = await generateInfo.mutateAsync({
          source_text: ideaSourceText(idea, ideasCtx),
        });
        set(i, { step: 'create' });
        const input = cardInputFromIdea(EMPTY_CARD_INPUT, info, idea, ideasCtx);
        cardId = await createCard.mutateAsync({ input, userId: user.id });
        set(i, { step: 'recto', cardId });
        await generateCard.mutateAsync({ card_id: cardId });
        set(i, { step: 'done', cardId });
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Erreur inconnue.';
        set(i, {
          step: 'error',
          cardId,
          error: cardId
            ? `Carte créée, mais le recto n’a pas pu être rédigé : ${message}`
            : message,
        });
      }
    }
    setRunning(false);
  }

  const toGenerate = [...selected].filter((i) => jobs[i]?.step !== 'done').length;

  return (
    <div
      className={cn(
        'mx-auto max-w-3xl px-4 py-6 sm:py-8',
        selected.size > 0 ? 'pb-[calc(env(safe-area-inset-bottom)+6rem)]' : 'pb-safe',
      )}
    >
      <BackLink />
      <header className="mb-5 flex items-center gap-2.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50">
          <Lightbulb className="h-5 w-5 text-amber-500" aria-hidden />
        </div>
        <div>
          <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
            Idées de SKILLCARDS
          </h1>
          <p className="text-ink-500 text-xs">
            L’IA propose, tu choisis les meilleures, les cartes sont créées en brouillon.
          </p>
        </div>
      </header>

      <form
        className="border-ink-100 space-y-4 rounded-2xl border bg-white p-4"
        onSubmit={(e) => {
          e.preventDefault();
          void propose();
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-ink-700 mb-1 block text-sm font-semibold">Niveau</span>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="border-ink-200 text-ink-900 h-11 w-full rounded-xl border bg-white px-3 text-sm"
            >
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-ink-700 mb-1 block text-sm font-semibold">
              Matière (facultatif)
            </span>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="border-ink-200 text-ink-900 h-11 w-full rounded-xl border bg-white px-3 text-sm"
            >
              <option value="">Selon le niveau</option>
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <fieldset>
          <legend className="text-ink-700 mb-1 text-sm font-semibold">Rareté</legend>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Rareté">
            {CARD_RARITIES.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={rarity === r}
                onClick={() => setRarity(r)}
                className={cn(
                  'flex min-h-[2.75rem] items-center gap-1.5 rounded-full border px-3 text-sm font-semibold active:scale-[0.98]',
                  rarity === r
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-ink-200 text-ink-700 bg-white',
                )}
              >
                {CARD_RARITY_LABELS[r]}
                <span className="flex" aria-hidden>
                  {Array.from({ length: CARD_RARITY_STARS[r] }, (_, k) => (
                    <Star key={k} className="h-3 w-3 fill-current" />
                  ))}
                </span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="text-ink-700 mb-1 block text-sm font-semibold">
              Thème (facultatif)
            </span>
            <input
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              maxLength={200}
              placeholder="Ex. : calcul littéral, probabilités, Python…"
              className="border-ink-200 text-ink-900 h-11 w-full rounded-xl border bg-white px-3 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-ink-700 mb-1 block text-sm font-semibold">Nombre d’idées</span>
            <select
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="border-ink-200 text-ink-900 h-11 w-full rounded-xl border bg-white px-3 text-sm sm:w-28"
            >
              {CARD_IDEA_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Button type="submit" fullWidth disabled={ideasQuery.isPending || running}>
          <span className="flex items-center justify-center gap-2">
            {ideas.length > 0 ? (
              <RotateCw className="h-4 w-4" aria-hidden />
            ) : (
              <Sparkles className="h-4 w-4" aria-hidden />
            )}
            {ideasQuery.isPending
              ? 'L’IA réfléchit…'
              : ideas.length > 0
                ? 'Proposer d’autres idées'
                : 'Proposer des idées'}
          </span>
        </Button>
        {ideasQuery.isError && (
          <p role="alert" className="text-sm text-red-600">
            {ideasQuery.error instanceof Error ? ideasQuery.error.message : 'Erreur inconnue.'}
          </p>
        )}
      </form>

      {ideas.length > 0 && ideasCtx && (
        <section className="mt-6 space-y-3" aria-label="Idées proposées">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-ink-900 text-base font-bold">
              {ideas.length} idées · {SCHOOL_LEVELS.find((l) => l.value === ideasCtx.level)?.label}{' '}
              · {CARD_RARITY_LABELS[ideasCtx.rarity]}
            </h2>
            <button
              type="button"
              onClick={() =>
                setSelected((prev) =>
                  prev.size === ideas.length ? new Set() : new Set(ideas.map((_, i) => i)),
                )
              }
              className="text-primary-600 ocean:text-primary-300 min-h-[2.75rem] px-2 text-sm font-semibold"
            >
              {selected.size === ideas.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
          <ul className="space-y-2">
            {ideas.map((idea, i) => (
              <IdeaRow
                key={`${idea.title}-${i}`}
                idea={idea}
                checked={selected.has(i)}
                job={jobs[i]}
                disabled={running}
                onToggle={() => toggle(i)}
              />
            ))}
          </ul>
        </section>
      )}

      {selected.size > 0 && (
        <div className="border-ink-100 ocean:bg-[#061433] fixed inset-x-0 bottom-0 z-30 border-t bg-white px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3">
          <div className="mx-auto max-w-3xl">
            <Button fullWidth disabled={running || toGenerate === 0} onClick={generateSelected}>
              {running
                ? 'Création des cartes…'
                : toGenerate === 0
                  ? 'Cartes créées'
                  : `Générer ${toGenerate} carte${toGenerate > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/tutor/cards"
      className="text-primary-600 ocean:text-primary-300 mb-4 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden /> SKILLCARDS
    </Link>
  );
}

function IdeaRow({
  idea,
  checked,
  job,
  disabled,
  onToggle,
}: {
  idea: CardIdea;
  checked: boolean;
  job?: IdeaJob;
  disabled: boolean;
  onToggle: () => void;
}) {
  const done = job?.step === 'done';
  return (
    <li
      className={cn(
        'rounded-2xl border bg-white p-4 transition-colors',
        checked ? 'border-primary-400 ring-primary-200 ring-1' : 'border-ink-100',
      )}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled || done}
        onClick={onToggle}
        className="flex w-full items-start gap-3 text-left active:scale-[0.99] disabled:active:scale-100"
      >
        <span
          className={cn(
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2',
            checked ? 'border-primary-500 bg-primary-500 text-white' : 'border-ink-300',
          )}
          aria-hidden
        >
          {checked && <Check className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-ink-900 block font-semibold">{idea.title}</span>
          <span className="mt-1 flex flex-wrap gap-1.5 text-xs">
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-600 rounded-full px-2 py-0.5">
              {subjectLabel(idea.subject)}
            </span>
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-600 rounded-full px-2 py-0.5">
              {CARD_TYPE_LABELS[idea.card_type] ?? idea.card_type}
            </span>
            <span className="bg-ink-50 ocean:bg-white/5 text-ink-600 rounded-full px-2 py-0.5">
              {idea.topic}
            </span>
          </span>
          <span className="text-ink-700 mt-2 block text-sm">{idea.pitch}</span>
          {idea.why && <span className="text-ink-500 mt-1 block text-xs italic">{idea.why}</span>}
        </span>
      </button>
      {job && job.step !== 'waiting' && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
          <span
            role="status"
            className={cn(
              'font-semibold',
              job.step === 'done' && 'ocean:text-green-300 text-green-600',
              job.step === 'error' && 'ocean:text-red-400 text-red-600',
              job.step !== 'done' && job.step !== 'error' && 'text-ink-500',
            )}
          >
            {job.step === 'error' ? job.error : IDEA_JOB_LABELS[job.step]}
          </span>
          {job.cardId && (job.step === 'done' || job.step === 'error') && (
            <Link
              to="/tutor/cards/$cardId"
              params={{ cardId: job.cardId }}
              className="text-primary-600 ocean:text-primary-300 min-h-[2.75rem] content-center font-semibold"
            >
              Ouvrir la carte ›
            </Link>
          )}
        </div>
      )}
      {job?.step === 'waiting' && (
        <p className="text-ink-400 mt-2 text-xs">{IDEA_JOB_LABELS.waiting}</p>
      )}
    </li>
  );
}
